import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Catches render errors. Lazy-loaded route chunks can fail to load after a
 * new deploy (the old HTML references a hash that no longer exists); when that
 * happens we reload once — guarded by a sessionStorage cooldown so we never
 * loop — to fetch the current chunk manifest.
 */
class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, _info: ErrorInfo) {
    const isChunkError =
      /Loading chunk|dynamically imported module|Importing a module script failed|Failed to fetch/i.test(
        `${error?.name} ${error?.message}`
      );
    const RELOAD_KEY = "chunk-reload-at";
    if (isChunkError) {
      const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
      if (Date.now() - last > 10000) {
        sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
        window.location.reload();
      }
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback ?? (
          <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-background px-4 text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground">Something went wrong</h1>
            <p className="text-muted-foreground">Please refresh the page to continue.</p>
            <button
              onClick={() => window.location.reload()}
              className="rounded-xl bg-primary px-6 py-3 font-semibold text-primary-foreground"
            >
              Reload
            </button>
          </div>
        )
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
